import { useQuery } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { DialogoPagoRubro } from "./DialogoPagoRubro"
import { FormularioImpuesto } from "./FormularioImpuesto"
import {
  type EstadoPago,
  formatearImporte,
  PanelRubros,
  type Rubro,
  type TipoRubro,
  totalDelRubro,
} from "./PanelRubros"

/**
 * Los rubros del expediente y su pago, montados sobre el detalle.
 *
 * Es el sitio donde el motor financiero de v2 se toca desde la operación: hasta que
 * este panel existió, `PanelRubros`, `ModalPago` y las tres vías de `pagar_rubro()`
 * estaban escritos y probados pero no había ninguna pantalla desde la que llegar a
 * ellos.
 *
 * ⚠ **El nombre del proveedor se resuelve aquí, no en el panel.** `RubroOut` trae
 * `proveedor_id` y nada más, y `PanelRubros` quiere el nombre porque una tabla de
 * gastos con identificadores numéricos no la lee nadie. Si el catálogo no carga
 * —porque este usuario no tiene `proveedor:ver`— la tabla sigue saliendo con un
 * guión en esa columna: perder el nombre de un proveedor no es motivo para dejar al
 * tramitador sin ver sus rubros.
 */

type RubroDeLaApi = {
  id: number
  expediente_id: number
  tipo: TipoRubro
  concepto: string
  proveedor_id: number | null
  cantidad: string
  precio_unitario: string
  moneda: "USD" | "BS"
  estado_pago: EstadoPago
  orden: number
}

type Proveedor = {
  id: number
  nombre: string
}

export function PanelFinancieroExpediente({ expedienteId }: { expedienteId: number }) {
  const usuario = usarSesion((e) => e.usuario)
  const [rubroEnPago, setRubroEnPago] = useState<number | null>(null)

  const rubros = useQuery({
    queryKey: ["rubros-expediente", expedienteId],
    queryFn: () => api.get<RubroDeLaApi[]>(`/api/v1/expedientes/${expedienteId}/rubros`),
  })

  const proveedores = useQuery({
    queryKey: ["proveedores"],
    queryFn: () => api.get<Proveedor[]>("/api/v1/proveedores"),
    // Sin el permiso esto sería un 403 garantizado en cada detalle de expediente.
    enabled: tienePermiso(usuario, "proveedor:ver"),
  })

  if (rubros.isPending) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando rubros…
      </p>
    )
  }

  if (rubros.isError) {
    return (
      <p
        role="alert"
        className="flex items-center gap-2 text-sm"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} aria-hidden={true} />
        No se pudieron cargar los rubros del expediente.
      </p>
    )
  }

  const nombreDe = new Map((proveedores.data ?? []).map((p) => [p.id, p.nombre]))

  const filas: Rubro[] = rubros.data.map((r) => ({
    id: r.id,
    tipo: r.tipo,
    concepto: r.concepto,
    proveedorNombre: r.proveedor_id === null ? null : (nombreDe.get(r.proveedor_id) ?? null),
    cantidad: r.cantidad,
    precioUnitario: r.precio_unitario,
    moneda: r.moneda,
    estadoPago: r.estado_pago,
  }))

  const enPago = rubros.data.find((r) => r.id === rubroEnPago) ?? null

  // Con la moneda aparte: un expediente mezcla rubros en dólares y en bolívares.
  const documento = (): DocumentoExportable => ({
    titulo: "Rubros del expediente",
    subtitulo: "Rubros, proveedores, estado de pago y total por moneda",
    secciones: [
      {
        titulo: "Rubros del expediente",
        columnas: [
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "Tipo", tipo: "texto" },
          { titulo: "Proveedor", tipo: "texto" },
          { titulo: "Cantidad", tipo: "numero" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Precio", tipo: "monto" },
          { titulo: "Total", tipo: "monto" },
          { titulo: "Estado", tipo: "texto" },
        ],
        filas: filas.map((r) => ({
          celdas: [
            r.concepto,
            r.tipo,
            r.tipo === "IMPUESTO" ? "Fisco" : r.proveedorNombre,
            Number(r.cantidad),
            r.moneda,
            Number(r.precioUnitario),
            Number(totalDelRubro(r)),
            r.estadoPago,
          ],
        })),
      },
    ],
  })

  return (
    <div className="space-y-4">
      {filas.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar
            titulo="Rubros del expediente"
            subtitulo="Rubros, proveedores, estado de pago y total por moneda"
            documento={documento}
          />
        </div>
      )}
      <PanelRubros
        rubros={filas}
        onPagar={tienePermiso(usuario, "pago:registrar") ? setRubroEnPago : undefined}
      />

      <TotalesPorMoneda rubros={rubros.data} />

      {/*
        El formulario va DEBAJO de la tabla y del total. Cargar un tributo es mirar
        primero lo que ya está cargado: ponerlo arriba invita a volver a cargar el
        que ya estaba, y un impuesto duplicado se paga dos veces.
      */}
      {tienePermiso(usuario, "rubro:editar") && <FormularioImpuesto expedienteId={expedienteId} />}

      {enPago && (
        <DialogoPagoRubro
          expedienteId={expedienteId}
          rubroId={enPago.id}
          concepto={enPago.concepto}
          total={totalDelRubro({
            cantidad: enPago.cantidad,
            precioUnitario: enPago.precio_unitario,
          })}
          moneda={enPago.moneda}
          onCerrar={() => setRubroEnPago(null)}
        />
      )}
    </div>
  )
}

/**
 * Suma de los rubros, **una línea por moneda**.
 *
 * ⚠ No se suman USD y BS en una sola cifra, y no es pedantería: convertirlos aquí
 * exigiría una tasa, y la tasa que valdría es la congelada a la apertura del
 * expediente —ni la de hoy ni la de la cotización—. Un total mezclado con la tasa
 * equivocada es una cifra que parece correcta y no lo es. Cuando haga falta el
 * consolidado, lo da el servidor con la tasa que corresponde.
 */
function TotalesPorMoneda({ rubros }: { rubros: RubroDeLaApi[] }) {
  const porMoneda = new Map<"USD" | "BS", number>()
  for (const r of rubros) {
    const total = Number(totalDelRubro({ cantidad: r.cantidad, precioUnitario: r.precio_unitario }))
    porMoneda.set(r.moneda, (porMoneda.get(r.moneda) ?? 0) + total)
  }

  if (porMoneda.size === 0) return null

  return (
    // Teñido con el lavado del acento y no una tarjeta más: es la cifra que cierra
    // la tabla de arriba, no una sección aparte. Sin ningún fondo quedaba flotando
    // entre dos bloques que sí lo tenían.
    <dl className="flex flex-wrap justify-end gap-x-6 gap-y-1 superficie px-4 py-2.5 text-sm">
      {[...porMoneda].map(([moneda, total]) => (
        <div key={moneda} className="flex gap-2">
          <dt style={{ color: "var(--fg-muted)" }}>Total en {moneda}</dt>
          <dd className="font-medium tabular-nums" data-testid={`total-${moneda}`}>
            {formatearImporte(total.toFixed(2), moneda, "-")}
          </dd>
        </div>
      ))}
    </dl>
  )
}
